import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  User,
  GraduationCap,
  Home,
  Settings,
  ClipboardList,
  Bookmark,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '~/components/ui/command';
import { Button } from '~/components/ui/button';
import { useQuickSearchQuery } from './useQuickSearchQuery';
import type { RoleContext } from '~/types';

interface CustomNavItem {
  id: string;
  name: string;
  group: string;
  url: string;
}

interface SearchItem {
  id: string;
  type: string;
  label: string;
  subtitle?: string;
  href: string;
  icon?: string;
}

interface QuickSearchProps {
  schoolYearId?: string;
  schoolSlug?: string;
  roleContext?: RoleContext | null;
  customNavItems?: CustomNavItem[];
  showTrigger?: boolean;
}

export interface QuickSearchHandle {
  open: () => void;
}

const QuickSearch = forwardRef<QuickSearchHandle, QuickSearchProps>(function QuickSearch(
  { schoolYearId, schoolSlug, roleContext, customNavItems = [], showTrigger = true },
  ref
) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }), []);

  const role = roleContext?.selectedRole;
  const isAdmin = role === 'PRIMARY_ADMIN' || role === 'ADMIN';
  const selectedYearSlug = roleContext?.selectedSchoolYear?.slug;

  // Quick search returns admin-only data (all families/students), and AppLayout mounts
  // this for every role — so only run it for admins. Non-admins (parents) would 401.
  const { data, loading } = useQuickSearchQuery(isAdmin ? schoolYearId : undefined);

  // Keyboard shortcut: Cmd+K (Mac) or Ctrl+K (Windows/Linux)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Build list of accessible pages based on role
  const pageItems = useMemo((): SearchItem[] => {
    if (!schoolSlug) return [];

    const pages: SearchItem[] = [
      {
        id: 'page-dashboard',
        type: 'page',
        label: 'Dashboard',
        href: `/app/${schoolSlug}/dashboard`,
        icon: 'home',
      },
    ];

    if (isAdmin) {
      pages.push(
        {
          id: 'page-families',
          type: 'page',
          label: 'Families',
          href: `/app/${schoolSlug}/families`,
          icon: 'users',
        },
        {
          id: 'page-settings',
          type: 'page',
          label: 'School Settings',
          href: `/app/${schoolSlug}/settings`,
          icon: 'settings',
        }
      );

      if (selectedYearSlug) {
        pages.push({
          id: 'page-enrollments',
          type: 'page',
          label: 'Enrollments',
          subtitle: roleContext?.selectedSchoolYear?.name,
          href: `/app/${schoolSlug}/years/${selectedYearSlug}/enrollments`,
          icon: 'enrollments',
        });
      }
    }

    return pages;
  }, [schoolSlug, isAdmin, selectedYearSlug, roleContext?.selectedSchoolYear?.name]);

  // Transform custom nav items into searchable items
  const quickLinkItems = useMemo((): SearchItem[] => {
    return customNavItems.map((item) => ({
      id: `quicklink-${item.id}`,
      type: 'quicklink',
      label: item.name,
      subtitle: item.group,
      href: item.url,
    }));
  }, [customNavItems]);

  // Transform API response into searchable items (families, guardians, students)
  const peopleItems = useMemo((): SearchItem[] => {
    if (!data?.schoolYear?.familyEnrollments) return [];

    const result: SearchItem[] = [];
    const seenFamilies = new Set<string>();
    const seenGuardians = new Set<string>();
    const seenStudents = new Set<string>();

    for (const enrollment of data.schoolYear.familyEnrollments) {
      const family = enrollment.family;
      if (!family) continue;

      if (!seenFamilies.has(family.id)) {
        seenFamilies.add(family.id);
        result.push({
          id: `family-${family.id}`,
          type: 'family',
          label: family.name,
          href: `/app/${schoolSlug}/families/${family.slug}`,
        });
      }

      if (family.guardians) {
        for (const guardian of family.guardians) {
          const person = guardian.person;
          if (!person || seenGuardians.has(person.id)) continue;
          seenGuardians.add(person.id);
          result.push({
            id: `guardian-${person.id}`,
            type: 'guardian',
            label: person.fullName,
            subtitle: family.name,
            href: `/app/${schoolSlug}/families/${family.slug}`,
          });
        }
      }

      if (enrollment.studentEnrollments) {
        for (const studentEnrollment of enrollment.studentEnrollments) {
          const person = studentEnrollment.person;
          if (!person || seenStudents.has(person.id)) continue;
          seenStudents.add(person.id);
          result.push({
            id: `student-${person.id}`,
            type: 'student',
            label: person.fullName,
            subtitle: family.name,
            href: `/app/${schoolSlug}/students/${person.id}`,
          });
        }
      }
    }

    return result;
  }, [data, schoolSlug]);

  const handleSelect = (href: string) => {
    navigate(href);
    setOpen(false);
  };

  const getIcon = (type: string, iconName?: string) => {
    switch (type) {
      case 'page':
        switch (iconName) {
          case 'home':
            return <Home className="h-4 w-4 text-muted-foreground" />;
          case 'users':
            return <Users className="h-4 w-4 text-muted-foreground" />;
          case 'settings':
            return <Settings className="h-4 w-4 text-muted-foreground" />;
          case 'enrollments':
            return <ClipboardList className="h-4 w-4 text-muted-foreground" />;
          default:
            return <Home className="h-4 w-4 text-muted-foreground" />;
        }
      case 'quicklink':
        return <Bookmark className="h-4 w-4 text-muted-foreground" />;
      case 'family':
        return <Users className="h-4 w-4 text-muted-foreground" />;
      case 'guardian':
        return <User className="h-4 w-4 text-muted-foreground" />;
      case 'student':
        return <GraduationCap className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };

  return (
    <>
      {showTrigger && (
        <Button
          variant="outline"
          className="relative h-9 w-full justify-start text-sm text-muted-foreground sm:pr-12 md:w-40 lg:w-64"
          onClick={() => setOpen(true)}
        >
          <Search className="mr-2 h-4 w-4" />
          Search...
          <kbd className="pointer-events-none absolute right-1.5 top-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
            <span className="text-xs">&#x2318;</span>K
          </kbd>
        </Button>
      )}

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages, families, students..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {pageItems.length > 0 && (
            <CommandGroup heading="Pages">
              {pageItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.label} ${item.subtitle || ''} page`}
                  onSelect={() => handleSelect(item.href)}
                >
                  {getIcon(item.type, item.icon)}
                  <div className="ml-2">
                    <div>{item.label}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground">
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {quickLinkItems.length > 0 && (
            <CommandGroup heading="Saved Views">
              {quickLinkItems.map((item) => (
                <CommandItem
                  key={item.id}
                  value={`${item.label} ${item.subtitle || ''} saved`}
                  onSelect={() => handleSelect(item.href)}
                >
                  {getIcon(item.type)}
                  <div className="ml-2">
                    <div>{item.label}</div>
                    {item.subtitle && (
                      <div className="text-xs text-muted-foreground">
                        {item.subtitle}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {loading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : (
            peopleItems.length > 0 && (
              <CommandGroup heading="People">
                {peopleItems.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.subtitle || ''}`}
                    onSelect={() => handleSelect(item.href)}
                  >
                    {getIcon(item.type)}
                    <div className="ml-2">
                      <div>{item.label}</div>
                      {item.subtitle && (
                        <div className="text-xs text-muted-foreground">
                          {item.subtitle}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
});

export default QuickSearch;
